/**
 * Admin redirects API module — CON-SEO-002 / PL-SEO-007.
 *
 * Endpoints covered:
 *   GET    /api/v1/admin/redirects             — paginated list
 *   POST   /api/v1/admin/redirects             — create redirect
 *   GET    /api/v1/admin/redirects/:id         — fetch single redirect
 *   PUT    /api/v1/admin/redirects/:id         — update redirect (full replace)
 *   DELETE /api/v1/admin/redirects/:id         — delete redirect
 *   POST   /api/v1/admin/redirects/import      — CSV bulk import
 *
 * Backend: PL-SEO-007 (endpoints confirmed present in admin.php).
 */
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  FetchRedirectsParams,
  RedirectCreatePayload,
  RedirectDTO,
  RedirectImportResponseData,
  RedirectImportResultDTO,
  RedirectListResponse,
  RedirectResponseData,
  RedirectUpdatePayload,
} from '@dfq/shared/types/redirects';

/** Snake_case pagination block emitted by meta.pagination in the platform envelope. */
interface WirePaginationMeta {
  page: number;
  per_page: number;
  total: number;
  last_page: number;
  has_more: boolean;
}

export async function fetchAdminRedirects(
  params: FetchRedirectsParams = {},
): Promise<RedirectListResponse> {
  try {
    const queryParams: Record<string, string | number | boolean | undefined> = {
      page: params.page,
      per_page: params.perPage,
      sort: params.sort,
      direction: params.direction,
      status_code: params.statusCode,
      is_active: params.isActive,
      search: params.search,
    };

    const res = await apiClient.get<
      ApiEnvelope<{ redirects: RedirectDTO[] }> & {
        meta: { pagination?: WirePaginationMeta };
      }
    >(
      '/admin/redirects',
      { params: queryParams },
    );

    const data = unwrap(res.data, res.status);
    const pagination = res.data.meta?.pagination;

    return {
      redirects: data.redirects,
      page: pagination?.page ?? 1,
      perPage: pagination?.per_page ?? 25,
      total: pagination?.total ?? 0,
      lastPage: pagination?.last_page ?? 1,
    } satisfies RedirectListResponse;
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function fetchAdminRedirect(id: number): Promise<RedirectDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<RedirectResponseData>>(
      `/admin/redirects/${id}`,
    );
    return unwrap(res.data, res.status).redirect;
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function createAdminRedirect(
  payload: RedirectCreatePayload,
): Promise<RedirectDTO> {
  try {
    const res = await apiClient.post<ApiEnvelope<RedirectResponseData>>(
      '/admin/redirects',
      payload,
    );
    return unwrap(res.data, res.status).redirect;
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function updateAdminRedirect(
  id: number,
  payload: RedirectUpdatePayload,
): Promise<RedirectDTO> {
  try {
    const res = await apiClient.put<ApiEnvelope<RedirectResponseData>>(
      `/admin/redirects/${id}`,
      payload,
    );
    return unwrap(res.data, res.status).redirect;
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function deleteAdminRedirect(id: number): Promise<void> {
  try {
    await apiClient.delete(`/admin/redirects/${id}`);
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function bulkImportAdminRedirects(
  file: File,
): Promise<RedirectImportResultDTO> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const res = await apiClient.post<ApiEnvelope<RedirectImportResponseData>>(
      '/admin/redirects/import',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return unwrap(res.data, res.status).import;
  } catch (err) {
    throwAsApiError(err);
  }
}
